import {
  createContext,
  type ReactNode,
  useCallback,
  useContext,
  useMemo,
  useState,
} from 'react';

export interface ChartError {
  chartId: string;
  chartTitle?: string;
  chartType: string;
  metric?: string;
  secondaryDimension?: string;
  reason:
    | 'no_data_provider'
    | 'chart_not_supported'
    | 'data_fetch_error'
    | 'unknown';
  timestamp: number;
}

interface ChartErrorContextValue {
  errors: ChartError[];
  reportError: (error: Omit<ChartError, 'timestamp'>) => void;
  clearErrors: () => void;
  clearError: (chartId: string) => void;
  hasErrors: boolean;
}

const ChartErrorContext = createContext<ChartErrorContextValue | null>(null);

export function ChartErrorProvider({ children }: { children: ReactNode }) {
  const [errors, setErrors] = useState<ChartError[]>([]);

  const reportError = useCallback((error: Omit<ChartError, 'timestamp'>) => {
    setErrors((prev) => {
      // Avoid duplicate errors for the same chart
      const existingIndex = prev.findIndex((e) => e.chartId === error.chartId);
      const newError: ChartError = {
        ...error,
        timestamp: Date.now(),
      };

      if (existingIndex >= 0) {
        // Update existing error
        const updated = [...prev];
        updated[existingIndex] = newError;
        return updated;
      }

      return [...prev, newError];
    });
  }, []);

  const clearErrors = useCallback(() => {
    setErrors([]);
  }, []);

  const clearError = useCallback((chartId: string) => {
    setErrors((prev) => prev.filter((e) => e.chartId !== chartId));
  }, []);

  const value = useMemo(
    () => ({
      errors,
      reportError,
      clearErrors,
      clearError,
      hasErrors: errors.length > 0,
    }),
    [errors, reportError, clearErrors, clearError]
  );

  return (
    <ChartErrorContext.Provider value={value}>
      {children}
    </ChartErrorContext.Provider>
  );
}

export function useChartErrors() {
  const context = useContext(ChartErrorContext);
  if (!context) {
    // Return no-op functions when not in provider (for non-AI preview contexts)
    return {
      errors: [],
      reportError: () => {
        // No-op: not in ChartErrorProvider context
      },
      clearErrors: () => {
        // No-op: not in ChartErrorProvider context
      },
      clearError: () => {
        // No-op: not in ChartErrorProvider context
      },
      hasErrors: false,
    };
  }
  return context;
}

export function useReportChartError() {
  const { reportError } = useChartErrors();
  return reportError;
}
